import { useEffect, useMemo, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { debounce } from 'lodash';
import { EditorState } from 'types';

const plugins = ['image', 'preview', 'code', 'fullscreen', 'table', 'wordcount', 'link'];
const toolbar = `bold italic  | fontfamily | fontsize | blocks | backcolor | alignleft aligncenter | alignright alignjustify | bullist numlist outdent indent | preview | code | image |link`;

type PropTypes = {
    initialValue: string;
    height: number;
    setEditorState: React.Dispatch<React.SetStateAction<EditorState | undefined>>;
    /**
     * Exposes the live editor instance once it initialises — used by the CRM
     * merge-tag menu to insert a tag at the caret (POGO-12603 Phase 6).
     * Optional, so every existing caller is unaffected.
     */
    onEditorInit?: (editor: TinyMCEEditor) => void;
    /**
     * Identity of the document being edited (record id, step id…). When it
     * changes the editor remounts and picks up the new initialValue — the
     * escape hatch for intentional content switches (loading a record,
     * switching templates) that the frozen initialValue would otherwise
     * ignore. Ordinary parent re-renders keep the same key and never remount.
     */
    documentKey?: string | number;
    /**
     * Grow the editor with its content instead of a fixed height with an inner
     * scrollbar (adds the `autoresize` plugin). `height` becomes the minimum.
     * Opt-in so existing fixed-height callers are unaffected.
     */
    autoResize?: boolean;
};

const TinyMCE = ({ initialValue, height, setEditorState, onEditorInit, documentKey, autoResize }: PropTypes) => {
    // TinyMCE is intentionally uncontrolled. Parent updates receive the live
    // HTML but must never feed a changed initialValue back into the mounted
    // editor, which would reset the caret.
    const initialValueRef = useRef(initialValue);
    const documentKeyRef = useRef(documentKey);
    if (documentKeyRef.current !== documentKey) {
        // an intentional document switch — unfreeze for the remount below
        documentKeyRef.current = documentKey;
        initialValueRef.current = initialValue;
    }
    const setEditorStateRef = useRef(setEditorState);
    setEditorStateRef.current = setEditorState;

    const debounceEditorChange = useMemo(
        () => debounce((value: string, editor: TinyMCEEditor) => setEditorStateRef.current({ rawHTML: value, editor }), 300),
        []
    );

    useEffect(() => () => debounceEditorChange.cancel(), [debounceEditorChange]);

    // Stable `init` reference — @tinymce/tinymce-react re-initialises the editor
    // when `init` changes identity, which (on a re-rendering parent like the
    // automation builder) reset the caret to the start of the line on every
    // debounced keystroke. Memoised so the editor is only built once.
    const init = useMemo(
        () => ({
            // autoresize grows the editor with its content (min = `height`);
            // otherwise `height` is a fixed frame with an inner scrollbar.
            ...(autoResize ? { min_height: height, autoresize_bottom_margin: 16 } : { height }),
            menubar: false,
            plugins: autoResize ? [...plugins, 'autoresize'] : plugins,
            toolbar,
            content_style: 'a { color: #4040FF !important; } body { font-family:Times New Roman,Arial,sans-serif; font-size:16px }'
        }),
        [height, autoResize]
    );

    return (
        <>
            <Editor
                key={documentKey === undefined ? undefined : String(documentKey)}
                apiKey={process.env.REACT_APP_TINY_MCE}
                initialValue={initialValueRef.current}
                init={init}
                onInit={(_evt, editor) => onEditorInit?.(editor)}
                onEditorChange={debounceEditorChange}
            />
        </>
    );
};

export default TinyMCE;
